import type { ComposerNode } from '@safetyhub/shared';

/**
 * Composer 선택 모델 — 클릭 해상도 순수 함수.
 *
 * 표준 group isolation 규칙(결정 기록 2026-07-06-composer-selection-model.md)을 계산한다:
 * - 단일 클릭: 현재 선택 컨텍스트(isolation root)를 기준으로, 클릭 지점(deepest)에서
 *   컨텍스트의 직속 자식까지만 선택(컨텍스트 없으면 body 직속 최상위 컨테이너).
 * - 더블 클릭: 선택된 컨테이너 안으로 드릴(컨텍스트 하강).
 *
 * 이 모듈은 순수 계산만 담당한다(상태/DOM/이벤트 없음). EditorPage 핸들러 배선은 P2.
 * 트리 순회 경계는 clone/collect 헬퍼와 동일하다:
 * group/stack=children, table=cells[].content, repeat=template, branch=branches[].children.
 */

/** 자식 컨테이너로 취급되는 노드 kind 집합 */
const CONTAINER_KINDS = new Set<ComposerNode['kind']>([
  'group',
  'stack',
  'table',
  'repeat',
  'branch',
]);

/**
 * 노드가 자식을 품는 컨테이너인지 판정한다(group/stack/table/repeat/branch).
 */
function isContainerKind(kind: ComposerNode['kind']): boolean {
  return CONTAINER_KINDS.has(kind);
}

/**
 * 한 노드의 직속 자식 노드 배열을 컨테이너 경계 규칙에 따라 반환한다.
 * 컨테이너가 아니면 빈 배열.
 * - group/stack → children
 * - table → cells[].content 를 순서대로 평탄화
 * - repeat → [template]
 * - branch → branches[].children 를 순서대로 평탄화
 */
function getChildNodes(node: ComposerNode): ComposerNode[] {
  switch (node.kind) {
    case 'group':
    case 'stack':
      return node.children;
    case 'table': {
      const out: ComposerNode[] = [];
      for (const cell of node.cells) {
        if (cell.content) out.push(...cell.content);
      }
      return out;
    }
    case 'repeat':
      return [node.template];
    case 'branch': {
      const out: ComposerNode[] = [];
      for (const slot of node.branches) out.push(...slot.children);
      return out;
    }
    default:
      return [];
  }
}

/**
 * body 트리 전체를 순회해 모든 노드 id → 부모 노드 id 맵을 만든다.
 * body 직속 노드의 부모는 `null`.
 *
 * 부모 판정은 컨테이너 경계 기준(getChildNodes와 동일):
 * group/stack children→부모=그 노드, table cells[].content→부모=table,
 * repeat template→부모=repeat, branch slots children→부모=branch.
 */
export function buildParentMap(body: ComposerNode[]): Map<string, string | null> {
  const map = new Map<string, string | null>();
  const walk = (nodes: ComposerNode[], parentId: string | null) => {
    for (const node of nodes) {
      map.set(node.id, parentId);
      const children = getChildNodes(node);
      if (children.length > 0) walk(children, node.id);
    }
  };
  walk(body, null);
  return map;
}

/**
 * 주어진 id에서 body 직속 조상까지의 조상 체인을 deepest→topLevel 순서로 반환한다.
 * 즉 `[id, 부모, 조부모, …, body직속]`.
 *
 * parentMap에 id가 없으면 방어적으로 `[id]`만 반환한다.
 * 체인 도중 parentMap에 없는 id를 만나면 그 지점에서 종료한다(순환/불완전 맵 방어).
 */
export function getAncestorChain(
  parentMap: Map<string, string | null>,
  id: string,
): string[] {
  if (!parentMap.has(id)) return [id];
  const chain: string[] = [];
  const seen = new Set<string>();
  let current: string | null | undefined = id;
  while (current != null && !seen.has(current) && parentMap.has(current)) {
    chain.push(current);
    seen.add(current);
    current = parentMap.get(current) ?? null;
  }
  return chain;
}

/**
 * 단일 클릭 시 선택할 노드 id를 계산한다.
 *
 * @param body       전체 노드 트리
 * @param deepestId  클릭이 실제로 적중한 최심부 노드 id
 * @param contextId  현재 선택 컨텍스트(isolation root). body 레벨이면 `null`
 * @returns          선택할 노드 id
 *
 * 알고리즘(chain = deepest→topLevel 조상 체인):
 * - `contextId === null` → chain 최상위(body 직속 조상)를 선택.
 * - `contextId`가 chain의 인덱스 k에 존재:
 *     - `k === 0`(컨텍스트 자체를 클릭) → `contextId`.
 *     - 그 외 → `chain[k-1]`(컨텍스트의 직속 자식, 클릭 방향).
 * - `contextId`가 chain에 없음(컨텍스트 밖 클릭) → chain 최상위(body 레벨로 이탈).
 *
 * deepestId가 트리에 없으면 deepestId를 그대로 반환한다(방어적).
 */
export function resolveSingleClickTarget(
  body: ComposerNode[],
  deepestId: string,
  contextId: string | null,
): string {
  const parentMap = buildParentMap(body);
  if (!parentMap.has(deepestId)) return deepestId;
  const chain = getAncestorChain(parentMap, deepestId);
  const topLevel = chain[chain.length - 1];

  if (contextId === null) return topLevel;

  const k = chain.indexOf(contextId);
  if (k === -1) return topLevel; // 컨텍스트 밖 클릭 → body 레벨 이탈
  if (k === 0) return contextId; // 컨텍스트 자체 클릭
  return chain[k - 1]; // 컨텍스트의 직속 자식
}

/**
 * body 트리에서 id에 해당하는 노드를 재귀 조회한다(컨테이너 경계 순회). 없으면 `null`.
 */
function findNode(body: ComposerNode[], id: string): ComposerNode | null {
  for (const node of body) {
    if (node.id === id) return node;
    const children = getChildNodes(node);
    if (children.length > 0) {
      const found = findNode(children, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 더블 클릭 드릴 결과를 계산한다.
 *
 * @returns `{ nextContextId, selectId }`
 *   - nextContextId: 드릴 후 선택 컨텍스트(isolation root)
 *   - selectId: 드릴 후 선택할 노드 id
 *
 * 알고리즘:
 * 1. `target = resolveSingleClickTarget(body, deepestId, contextId)`.
 * 2. target 노드가 컨테이너이고 `target !== deepestId`이면 그 안으로 진입:
 *    `{ nextContextId: target, selectId: resolveSingleClickTarget(body, deepestId, target) }`.
 * 3. 그 외(리프이거나 이미 최심부 컨테이너) → 컨텍스트 불변, target 선택:
 *    `{ nextContextId: contextId, selectId: target }`.
 *    (편집 진입은 P2 핸들러 몫)
 */
export function resolveDrillTarget(
  body: ComposerNode[],
  deepestId: string,
  contextId: string | null,
): { nextContextId: string | null; selectId: string } {
  const target = resolveSingleClickTarget(body, deepestId, contextId);
  const targetNode = findNode(body, target);

  if (targetNode && isContainerKind(targetNode.kind) && target !== deepestId) {
    return {
      nextContextId: target,
      selectId: resolveSingleClickTarget(body, deepestId, target),
    };
  }

  return { nextContextId: contextId, selectId: target };
}
